import { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/AdminSidebar';
import '../styles/AdminAccountPage.css';

import {
    UserCircle,
    Save,
    KeyRound,
    Mail,
    Phone,
    Building2,
    MapPin,
    FileSignature,
    Shield,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

// Uwaga: ścieżki endpointów są przykładowe — pod backend podmienisz je jak chcesz.
const ENDPOINTS = {
    me: `${API}/admin/account/me`,
    updateCard: `${API}/admin/account/card`,
    updateLogin: `${API}/admin/account/login`,
    updatePassword: `${API}/admin/account/password`,
};

export default function AdminAccountPage() {
    const [loading, setLoading] = useState(true);

    // komunikaty
    const [info, setInfo] = useState('');
    const [error, setError] = useState('');

    // stan – wizytówka
    const [card, setCard] = useState({
        displayName: '',
        position: '',
        company: 'PSDiA',
        phone: '',
        email: '',
        city: '',
        signature: '',
        bio: '',
    });

    // stan – login/email
    const [loginData, setLoginData] = useState({
        email: '',
        username: '', // opcjonalnie, jeśli masz username
    });

    // stan – hasło
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    // zapis w toku
    const [saving, setSaving] = useState({
        card: false,
        login: false,
        password: false,
    });

    const token = useMemo(() => localStorage.getItem('token'), []);

    useEffect(() => {
        const fetchMe = async () => {
            setLoading(true);
            setError('');
            setInfo('');

            try {
                // jeśli backend jeszcze nie gotowy – po prostu zostaną puste pola
                const res = await fetch(ENDPOINTS.me, {
                    method: 'GET',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });

                if (!res.ok) {
                    // nie wywalamy błędu hard, bo backend może jeszcze nie mieć endpointu
                    setLoading(false);
                    return;
                }

                const data = await res.json();

                // Bezpieczne mapowanie (nie zakładamy 1:1 struktury)
                setCard((prev) => ({
                    ...prev,
                    displayName: data?.card?.displayName ?? data?.displayName ?? prev.displayName,
                    position: data?.card?.position ?? data?.position ?? prev.position,
                    company: data?.card?.company ?? data?.company ?? prev.company,
                    phone: data?.card?.phone ?? data?.phone ?? prev.phone,
                    email: data?.card?.email ?? data?.email ?? prev.email,
                    city: data?.card?.city ?? data?.city ?? prev.city,
                    signature: data?.card?.signature ?? data?.signature ?? prev.signature,
                    bio: data?.card?.bio ?? data?.bio ?? prev.bio,
                }));

                setLoginData((prev) => ({
                    ...prev,
                    email: data?.email ?? prev.email,
                    username: data?.username ?? prev.username,
                }));
            } catch (e) {
                // też bez spiny — frontend ma działać nawet bez backendu
                console.error(e);
            } finally {
                setLoading(false);
            }
        };

        fetchMe();
    }, [token]);

    const setField = (setter) => (e) => {
        const { name, value } = e.target;
        setter((prev) => ({ ...prev, [name]: value }));
    };

    // Format phone number to xxx-xxx-xxx pattern
    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const digits = value.replace(/\D/g, '');
        // Limit to 9 digits
        const limitedDigits = digits.slice(0, 9);
        // Add dashes in xxx-xxx-xxx format
        if (limitedDigits.length <= 3) {
            return limitedDigits;
        } else if (limitedDigits.length <= 6) {
            return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
        } else {
            return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
        }
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setCard((prev) => ({ ...prev, phone: formatted }));
    };

    const request = async (url, method, body, isFormData = false) => {
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        if (!isFormData) headers['Content-Type'] = 'application/json';

        const res = await fetch(url, {
            method,
            headers,
            body: isFormData ? body : JSON.stringify(body),
        });

        if (!res.ok) {
            let msg = 'Wystąpił błąd zapisu.';
            try {
                const data = await res.json();
                msg = data?.message || msg;
            } catch (_) { }
            throw new Error(msg);
        }

        try {
            return await res.json();
        } catch (_) {
            return null;
        }
    };

    const handleSaveCard = async () => {
        setSaving((p) => ({ ...p, card: true }));
        setInfo('');
        setError('');

        try {
            await request(ENDPOINTS.updateCard, 'PUT', { card });
            setInfo('Zapisano wizytówkę.');
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving((p) => ({ ...p, card: false }));
        }
    };

    const handleSaveLogin = async () => {
        setSaving((p) => ({ ...p, login: true }));
        setInfo('');
        setError('');

        try {
            if (!loginData.email?.trim()) {
                throw new Error('Podaj poprawny email/login.');
            }
            await request(ENDPOINTS.updateLogin, 'PUT', { login: loginData });
            setInfo('Zapisano dane logowania.');
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving((p) => ({ ...p, login: false }));
        }
    };

    const handleChangePassword = async () => {
        setSaving((p) => ({ ...p, password: true }));
        setInfo('');
        setError('');

        try {
            if (!passwordData.currentPassword || !passwordData.newPassword) {
                throw new Error('Uzupełnij obecne i nowe hasło.');
            }
            if (passwordData.newPassword.length < 8) {
                throw new Error('Nowe hasło powinno mieć minimum 8 znaków.');
            }
            if (passwordData.newPassword !== passwordData.confirmPassword) {
                throw new Error('Nowe hasło i potwierdzenie nie są takie same.');
            }

            await request(ENDPOINTS.updatePassword, 'PUT', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });

            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setInfo('Hasło zostało zmienione.');
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving((p) => ({ ...p, password: false }));
        }
    };

    return (
        <div className="admin-account-page">
            <Sidebar />

            <div className="main-content">
                <div className="admin-account-inner">
                    <header className="aa-header">
                        <div>
                            <h1>Moje konto</h1>
                            <p className="aa-subtitle">
                                Uzupełnij wizytówkę, zmień dane logowania lub hasło.
                            </p>
                        </div>
                    </header>

                    {(info || error) && (
                        <div className={`aa-alert ${error ? 'aa-alert--error' : 'aa-alert--ok'}`}>
                            {error || info}
                        </div>
                    )}

                    {loading ? (
                        <div className="aa-loading">Ładowanie danych konta…</div>
                    ) : (
                        <div className="aa-grid">
                            {/* === WIZYTÓWKA === */}
                            <section className="aa-card">
                                <div className="aa-card-head">
                                    <div className="aa-card-title">
                                        <UserCircle size={18} />
                                        Wizytówka
                                    </div>
                                    <button
                                        className="aa-btn aa-btn-primary"
                                        type="button"
                                        onClick={handleSaveCard}
                                        disabled={saving.card}
                                    >
                                        <Save size={16} />
                                        {saving.card ? 'Zapisuję…' : 'Zapisz'}
                                    </button>
                                </div>

                                <div className="aa-form">
                                    <div className="aa-row">
                                        <div className="aa-field">
                                            <label>Imię i nazwisko (wyświetlane)</label>
                                            <input
                                                name="displayName"
                                                value={card.displayName}
                                                onChange={setField(setCard)}
                                                placeholder="np. Jan Nowak"
                                            />
                                        </div>

                                        <div className="aa-field">
                                            <label>Stanowisko</label>
                                            <div className="aa-input-icon">
                                                <Building2 size={16} />
                                                <input
                                                    name="position"
                                                    value={card.position}
                                                    onChange={setField(setCard)}
                                                    placeholder="np. Administrator / Koordynator"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="aa-row">
                                        <div className="aa-field">
                                            <label>Telefon</label>
                                            <div className="aa-input-icon">
                                                <Phone size={16} />
                                                <input
                                                    name="phone"
                                                    type="tel"
                                                    value={card.phone}
                                                    onChange={handlePhoneChange}
                                                    placeholder="123-456-789"
                                                    maxLength={11}
                                                />
                                            </div>
                                        </div>

                                        <div className="aa-field">
                                            <label>Email</label>
                                            <div className="aa-input-icon">
                                                <Mail size={16} />
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={card.email}
                                                    onChange={setField(setCard)}
                                                    placeholder="email@example.com"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="aa-row">
                                        <div className="aa-field">
                                            <label>Miasto</label>
                                            <div className="aa-input-icon">
                                                <MapPin size={16} />
                                                <input
                                                    name="city"
                                                    value={card.city}
                                                    onChange={setField(setCard)}
                                                    placeholder="Kraków"
                                                />
                                            </div>
                                        </div>

                                        <div className="aa-field">
                                            <label>Podpis (np. do wiadomości)</label>
                                            <div className="aa-input-icon">
                                                <FileSignature size={16} />
                                                <input
                                                    name="signature"
                                                    value={card.signature}
                                                    onChange={setField(setCard)}
                                                    placeholder="Pozdrawiam, …"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="aa-field">
                                        <label>Krótka notka</label>
                                        <textarea
                                            name="bio"
                                            value={card.bio}
                                            onChange={setField(setCard)}
                                            placeholder="2–3 zdania o Tobie / Twojej roli w systemie."
                                            rows={4}
                                        />
                                    </div>

                                    <div className="aa-note">
                                        Tip: ta wizytówka może być używana np. w podpisach wiadomości,
                                        w profilu systemowym albo przy eksporcie raportów.
                                    </div>
                                </div>
                            </section>

                            {/* === DANE LOGOWANIA + HASŁO === */}
                            <div className="aa-col">
                                <section className="aa-card">
                                    <div className="aa-card-head">
                                        <div className="aa-card-title">
                                            <Mail size={18} />
                                            Dane logowania
                                        </div>
                                        <button
                                            className="aa-btn"
                                            type="button"
                                            onClick={handleSaveLogin}
                                            disabled={saving.login}
                                        >
                                            <Save size={16} />
                                            {saving.login ? 'Zapisuję…' : 'Zapisz'}
                                        </button>
                                    </div>

                                    <div className="aa-form">
                                        <div className="aa-field">
                                            <label>Email / login</label>
                                            <div className="aa-input-icon">
                                                <Mail size={16} />
                                                <input
                                                    name="email"
                                                    value={loginData.email}
                                                    onChange={setField(setLoginData)}
                                                    placeholder="email@login.pl"
                                                />
                                            </div>
                                        </div>

                                        <div className="aa-field">
                                            <label>Username (opcjonalnie)</label>
                                            <input
                                                name="username"
                                                value={loginData.username}
                                                onChange={setField(setLoginData)}
                                                placeholder="np. admin01"
                                            />
                                        </div>

                                        <div className="aa-note">
                                            Jeżeli masz logowanie tylko po emailu — username możesz olać.
                                        </div>
                                    </div>
                                </section>

                                <section className="aa-card">
                                    <div className="aa-card-head">
                                        <div className="aa-card-title">
                                            <Shield size={18} />
                                            Bezpieczeństwo
                                        </div>
                                    </div>

                                    <div className="aa-form">
                                        <div className="aa-field">
                                            <label>Obecne hasło</label>
                                            <div className="aa-input-icon">
                                                <KeyRound size={16} />
                                                <input
                                                    type="password"
                                                    name="currentPassword"
                                                    value={passwordData.currentPassword}
                                                    onChange={setField(setPasswordData)}
                                                    placeholder="••••••••"
                                                />
                                            </div>
                                        </div>

                                        <div className="aa-row">
                                            <div className="aa-field">
                                                <label>Nowe hasło</label>
                                                <input
                                                    type="password"
                                                    name="newPassword"
                                                    value={passwordData.newPassword}
                                                    onChange={setField(setPasswordData)}
                                                    placeholder="min. 8 znaków"
                                                />
                                            </div>
                                            <div className="aa-field">
                                                <label>Potwierdź hasło</label>
                                                <input
                                                    type="password"
                                                    name="confirmPassword"
                                                    value={passwordData.confirmPassword}
                                                    onChange={setField(setPasswordData)}
                                                    placeholder="powtórz"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            className="aa-btn aa-btn-primary aa-btn-full"
                                            type="button"
                                            onClick={handleChangePassword}
                                            disabled={saving.password}
                                        >
                                            <KeyRound size={16} />
                                            {saving.password ? 'Zmieniam…' : 'Zmień hasło'}
                                        </button>

                                        <div className="aa-note">
                                            Hasło powinno mieć minimum 8 znaków. Docelowo możesz dopiąć wymagania:
                                            wielka litera, cyfra, znak specjalny, itd.
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
